import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import { CommissionTier } from '../../../hooks/admin/useAdminCommissions';
import {
  INK,
  FONTS,
  primaryBtnSx,
  ghostBtnSx,
  ledgerInputSx,
  ledgerSelectSx,
  neutralChipSx,
  editorialDialogTitleSx,
  fieldLabelSx,
} from '../../../styles/ledger';

interface TierEditorProps {
  tiers: CommissionTier[];
  onChange: (tiers: CommissionTier[]) => void;
  error?: string;
  disabled?: boolean;
}

const generateBulkTiers = (
  count: number,
  startWeight: number,
  interval: number,
  value: number,
  valueType: 'fixed' | 'percentage',
  increment: number = 0
): CommissionTier[] => {
  const tiers: CommissionTier[] = [];
  for (let i = 0; i < count; i++) {
    const weightMin = startWeight + i * interval;
    const weightMax = i === count - 1 ? null : weightMin + interval;
    const valueAmount = value + i * increment;
    tiers.push({ weight_min: weightMin, weight_max: weightMax, value_amount: valueAmount, value_type: valueType });
  }
  return tiers;
};

/** Weight-tier table with inline editing, reorder, clone, and bulk generation. */
const TierEditor: React.FC<TierEditorProps> = ({ tiers, onChange, error, disabled }) => {
  const [bulkInsertOpen, setBulkInsertOpen] = useState(false);

  const patchTier = (index: number, patch: Partial<CommissionTier>) =>
    onChange(tiers.map((t, i) => (i === index ? { ...t, ...patch } : t)));

  const swapTiers = (a: number, b: number) => {
    const next = [...tiers];
    [next[a], next[b]] = [next[b], next[a]];
    onChange(next);
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography
          sx={{
            fontFamily: FONTS.mono,
            fontSize: 10.5,
            color: INK.subtle,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
          }}
        >
          {tiers.length} {tiers.length === 1 ? 'tier' : 'tiers'}
        </Typography>
        <Button size="small" disabled={disabled} onClick={() => setBulkInsertOpen(true)} sx={ghostBtnSx}>
          Bulk insert
        </Button>
      </Stack>

      {error && (
        <Typography sx={{ fontFamily: FONTS.sans, fontSize: 11, color: INK.inactive, display: 'block', mb: 1 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ border: `1px solid ${INK.hairline}`, borderRadius: 0.5, overflow: 'hidden', mb: 1.5, bgcolor: INK.surface }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  '& .MuiTableCell-head': {
                    fontFamily: FONTS.mono,
                    fontSize: 10,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: INK.subtle,
                    fontWeight: 500,
                    borderBottom: `1px solid ${INK.hairline}`,
                    bgcolor: INK.surfaceTint,
                    py: 1.25,
                  },
                }}
              >
                <TableCell width={48}>№</TableCell>
                <TableCell>Min kg</TableCell>
                <TableCell>Max kg</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right" width={130}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tiers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5, borderBottom: 'none' }}>
                    <Typography sx={{ fontFamily: FONTS.serif, fontSize: 20, color: INK.muted, letterSpacing: '-0.01em', mb: 0.5 }}>
                      No tiers yet.
                    </Typography>
                    <Typography sx={{ fontFamily: FONTS.sans, fontSize: 12, color: INK.subtle }}>
                      Add a tier row below or use bulk insert.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tiers.map((tier, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${INK.hairlineSoft}`,
                        py: 1.5,
                      },
                      '&:hover': { bgcolor: INK.surfaceTint },
                      '&:last-of-type .MuiTableCell-root': { borderBottom: 'none' },
                    }}
                  >
                    <TableCell>
                      <Stack direction="column" alignItems="center" spacing={0}>
                        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.muted, fontWeight: 500 }}>
                          {String(index + 1).padStart(2, '0')}
                        </Typography>
                        <Stack direction="row" spacing={0} sx={{ mt: 0.5 }}>
                          <Tooltip title="Move up">
                            <span>
                              <IconButton
                                size="small"
                                disabled={disabled || index === 0}
                                onClick={() => swapTiers(index - 1, index)}
                                sx={{ p: 0.25, color: INK.subtle, '&:hover': { color: INK.accent, bgcolor: INK.accentSoft } }}
                              >
                                <ArrowUpIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move down">
                            <span>
                              <IconButton
                                size="small"
                                disabled={disabled || index === tiers.length - 1}
                                onClick={() => swapTiers(index, index + 1)}
                                sx={{ p: 0.25, color: INK.subtle, '&:hover': { color: INK.accent, bgcolor: INK.accentSoft } }}
                              >
                                <ArrowDownIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        disabled={disabled}
                        value={tier.weight_min}
                        onChange={(e) => patchTier(index, { weight_min: parseFloat(e.target.value) || 0 })}
                        InputProps={{ inputProps: { min: 0, step: 'any' } }}
                        sx={{ ...ledgerInputSx, width: 100 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        disabled={disabled}
                        value={tier.weight_max === null ? '' : tier.weight_max}
                        onChange={(e) =>
                          patchTier(index, { weight_max: e.target.value === '' ? null : parseFloat(e.target.value) })
                        }
                        placeholder="∞"
                        InputProps={{ inputProps: { min: 0, step: 'any' } }}
                        sx={{ ...ledgerInputSx, width: 100 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        disabled={disabled}
                        value={tier.value_amount}
                        onChange={(e) => patchTier(index, { value_amount: parseFloat(e.target.value) || 0 })}
                        InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                        sx={{ ...ledgerInputSx, width: 110 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        disabled={disabled}
                        value={tier.value_type}
                        onChange={(e) => patchTier(index, { value_type: e.target.value as 'fixed' | 'percentage' })}
                        sx={{ ...ledgerSelectSx, width: 130 }}
                      >
                        <MenuItem value="fixed" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>ILS</MenuItem>
                        <MenuItem value="percentage" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>Percent</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Clone tier">
                          <span>
                            <IconButton
                              size="small"
                              disabled={disabled}
                              onClick={() => onChange([...tiers, { ...tier }])}
                              sx={{ width: 28, height: 28, borderRadius: 0.5, color: INK.muted, '&:hover': { bgcolor: INK.accentSoft, color: INK.accent } }}
                            >
                              <ContentCopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete tier">
                          <span>
                            <IconButton
                              size="small"
                              disabled={disabled}
                              onClick={() => onChange(tiers.filter((_, i) => i !== index))}
                              sx={{ width: 28, height: 28, borderRadius: 0.5, color: INK.muted, '&:hover': { bgcolor: INK.inactiveSoft, color: INK.inactive } }}
                            >
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Button
        disabled={disabled}
        onClick={() => onChange([...tiers, { weight_min: 0, weight_max: null, value_amount: 5, value_type: 'percentage' }])}
        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
        fullWidth
        sx={{
          ...ghostBtnSx,
          py: 1.2,
          borderStyle: 'dashed',
          color: INK.muted,
          '&:hover': { borderColor: INK.accent, color: INK.accent, bgcolor: INK.accentSoft, borderStyle: 'dashed' },
        }}
      >
        Add tier
      </Button>

      <BulkInsertModal
        open={bulkInsertOpen}
        onClose={() => setBulkInsertOpen(false)}
        onConfirm={(generated) => {
          onChange([...tiers, ...generated]);
          setBulkInsertOpen(false);
        }}
      />
    </>
  );
};

/* ─────────────────────────────────────────────
   BULK INSERT MODAL — generates stepped tiers
   ───────────────────────────────────────────── */
interface BulkInsertModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (tiers: CommissionTier[]) => void;
}

const BulkInsertModal: React.FC<BulkInsertModalProps> = ({ open, onClose, onConfirm }) => {
  const [count, setCount] = useState(5);
  const [startWeight, setStartWeight] = useState(0);
  const [interval, setIntervalValue] = useState(5);
  const [value, setValue] = useState(5);
  const [valueType, setValueType] = useState<'fixed' | 'percentage'>('percentage');
  const [increment, setIncrement] = useState(0);
  const [previewTiers, setPreviewTiers] = useState<CommissionTier[]>([]);

  useEffect(() => {
    if (open) {
      setPreviewTiers(generateBulkTiers(count, startWeight, interval, value, valueType, increment));
    }
  }, [open, count, startWeight, interval, value, valueType, increment]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 0.5, bgcolor: INK.bg, backgroundImage: 'none' } }}
    >
      <DialogTitle sx={editorialDialogTitleSx}>
        Bulk insert tiers
        <Typography sx={{ display: 'block', fontFamily: FONTS.sans, fontSize: 12, color: INK.muted, fontWeight: 400, mt: 0.5 }}>
          Generate multiple weight bands automatically — useful for stepped commission ladders.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, fontFamily: FONTS.sans }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography sx={fieldLabelSx}>Number of tiers</Typography>
            <TextField
              type="number"
              value={count}
              onChange={(e) => setCount(parseInt(e.target.value) || 1)}
              fullWidth
              size="small"
              InputProps={{ inputProps: { min: 1, max: 20 } }}
              helperText="Max 20"
              sx={ledgerInputSx}
            />
          </Box>
          <Box>
            <Typography sx={fieldLabelSx}>Starting weight (kg)</Typography>
            <TextField
              type="number"
              value={startWeight}
              onChange={(e) => setStartWeight(parseFloat(e.target.value) || 0)}
              fullWidth
              size="small"
              InputProps={{ inputProps: { min: 0, step: 'any' } }}
              helperText="First tier minimum"
              sx={ledgerInputSx}
            />
          </Box>
          <Box>
            <Typography sx={fieldLabelSx}>Weight interval (kg)</Typography>
            <TextField
              type="number"
              value={interval}
              onChange={(e) => setIntervalValue(parseFloat(e.target.value) || 1)}
              fullWidth
              size="small"
              InputProps={{ inputProps: { min: 0.001, step: 'any' } }}
              helperText="Width of each band"
              sx={ledgerInputSx}
            />
          </Box>
          <Box>
            <Typography sx={fieldLabelSx}>Starting value</Typography>
            <TextField
              type="number"
              value={value}
              onChange={(e) => setValue(parseFloat(e.target.value) || 0)}
              fullWidth
              size="small"
              InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              helperText="Commission for first tier"
              sx={ledgerInputSx}
            />
          </Box>
          <Box>
            <Typography sx={fieldLabelSx}>Value type</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={valueType}
                onChange={(e) => setValueType(e.target.value as 'fixed' | 'percentage')}
                sx={ledgerSelectSx}
              >
                <MenuItem value="fixed" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>Fixed (ILS)</MenuItem>
                <MenuItem value="percentage" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>Percentage (%)</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography sx={fieldLabelSx}>Per-tier increment</Typography>
            <TextField
              type="number"
              value={increment}
              onChange={(e) => setIncrement(parseFloat(e.target.value) || 0)}
              fullWidth
              size="small"
              InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              helperText="Optional progressive bump"
              sx={ledgerInputSx}
            />
          </Box>
        </Box>

        <Box
          sx={{
            border: `1px solid ${INK.hairline}`,
            borderRadius: 0.5,
            bgcolor: INK.surface,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${INK.hairline}`, bgcolor: INK.surfaceTint }}>
            <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: '0.16em', color: INK.subtle, textTransform: 'uppercase' }}>
              Preview · {previewTiers.length} tiers
            </Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 280 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow
                  sx={{
                    '& .MuiTableCell-head': {
                      fontFamily: FONTS.mono,
                      fontSize: 10,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      color: INK.subtle,
                      fontWeight: 500,
                      bgcolor: INK.surface,
                      borderBottom: `1px solid ${INK.hairlineSoft}`,
                    },
                  }}
                >
                  <TableCell>№</TableCell>
                  <TableCell>Min</TableCell>
                  <TableCell>Max</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previewTiers.map((tier, index) => (
                  <TableRow key={index} sx={{ '& .MuiTableCell-root': { borderBottom: `1px solid ${INK.hairlineSoft}`, py: 1.2 } }}>
                    <TableCell sx={{ fontFamily: FONTS.mono, fontSize: 11.5, color: INK.muted }}>
                      {String(index + 1).padStart(2, '0')}
                    </TableCell>
                    <TableCell sx={{ fontFamily: FONTS.mono, fontSize: 12.5, color: INK.text }}>
                      {tier.weight_min} kg
                    </TableCell>
                    <TableCell sx={{ fontFamily: FONTS.mono, fontSize: 12.5, color: INK.text }}>
                      {tier.weight_max === null ? '∞' : `${tier.weight_max} kg`}
                    </TableCell>
                    <TableCell sx={{ fontFamily: FONTS.mono, fontSize: 12.5, color: INK.text, fontWeight: 600 }}>
                      {tier.value_amount}
                      {tier.value_type === 'percentage' ? '%' : ''}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tier.value_type === 'fixed' ? 'ILS' : '%'}
                        size="small"
                        sx={neutralChipSx}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1, borderTop: `1px solid ${INK.hairlineSoft}` }}>
        <Button onClick={onClose} sx={ghostBtnSx}>Cancel</Button>
        <Button onClick={() => onConfirm(previewTiers)} sx={primaryBtnSx} disabled={previewTiers.length === 0}>
          Add {previewTiers.length} tiers
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TierEditor;
